import styled, { css } from 'foundations-theming-specialty/utils';
import useVerticalNavKeyboardControls from './hooks/useVerticalNavKeyboardControls';
import useNavItems from '../../hooks/useNavItems';
import VerticalNavMenuItem from './VerticalNavMenuItem';
import useActiveNavItemObserver from './hooks/useActiveNavItemObserver';
import useVerticalNavPrimaryMenuItemMouseBehaviors from './hooks/useVerticalNavPrimaryMenuItemMouseBehaviors';
import getIsSettingsPage from '../../utils/redesignedNavHacks/getIsSettingsPage';
import { GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_EXPANDED_WIDTH } from 'nav-meta/constants';
import { useEffect } from 'react';
import { NAV_ITEM_BOOKMARKS_GROUP_ID } from '../../constants/Bookmarks';
import usePrevious from '../../hooks/usePrevious';
import { useComponentState } from 'navigation-components/platform/hooks';
import { GLOBAL_SEARCH_COMPONENT } from 'navigation-components/config';
import useIsVerticalNavPinned from '../../hooks/useIsVerticalNavPinned';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUl = styled.ul.withConfig({
  displayName: "VerticalNavMenu__StyledUl",
  componentId: "dpbye0-0"
})(["list-style:none;padding-left:0;line-height:16px;margin-bottom:0;height:100%;width:", ";"], GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_EXPANDED_WIDTH);
const StyledNav = styled.nav.withConfig({
  displayName: "VerticalNavMenu__StyledNav",
  componentId: "dpbye0-1"
})(({
  $isGlobalSearchOpen,
  theme
}) => {
  return css(["height:100%;", ""], !$isGlobalSearchOpen && css(["&::after{width:18px;height:18px;top:0;right:-18px;position:absolute;border-top-left-radius:50%;content:'';box-shadow:-9px -1px 0 0 ", ";pointer-events:none;}"], theme.color['global-shell-fill-surface-default']));
});

// intended to stop users from accidentally opening secondary menus
const BlockingElement = styled.div.withConfig({
  displayName: "VerticalNavMenu__BlockingElement",
  componentId: "dpbye0-2"
})(["position:absolute;height:100%;width:24px;top:0;right:0;background:transparent;"]);
const VerticalNavMenu = ({
  isFocusInside = true
}) => {
  const {
    isOpen
  } = useComponentState(GLOBAL_SEARCH_COMPONENT);
  const {
    byId,
    primaryNav,
    openMenus,
    activeItem
  } = useNavItems();
  const wasFocusPreviouslyInside = usePrevious(isFocusInside);
  const isASecondaryMenuOpen = !!openMenus.primary;
  const isVerticalNavPinned = useIsVerticalNavPinned();
  const displayBlockingElement = !isVerticalNavPinned && !(openMenus.primary || primaryNav.openTooltip);
  useActiveNavItemObserver();
  const {
    onClick,
    onMouseEnter,
    onMouseEnterLink,
    onMouseLeaveLink,
    setIsMouseMovingTowardsSubmenu
  } = useVerticalNavPrimaryMenuItemMouseBehaviors();
  const {
    onKeyDown
  } = useVerticalNavKeyboardControls();
  useEffect(() => {
    if (isVerticalNavPinned && !isFocusInside && openMenus.primary || !isVerticalNavPinned && !isFocusInside) {
      onClick()();
    }
  }, [isFocusInside, isVerticalNavPinned, onClick, openMenus.primary, wasFocusPreviouslyInside]);
  return /*#__PURE__*/_jsxs(StyledNav, {
    $isGlobalSearchOpen: isOpen,
    children: [/*#__PURE__*/_jsx(StyledUl, {
      role: "menu",
      className: "primary-nav-menu",
      children: primaryNav.navItems.map(({
        id
      }) => {
        const {
          label,
          path,
          children
        } = byId[id];
        const isBookmarksMenu = id === NAV_ITEM_BOOKMARKS_GROUP_ID;
        const hasSubmenu = children.length > 0 || isBookmarksMenu;
        const isSubmenuOpen = hasSubmenu && openMenus.primary === id;
        const shouldHighlight = activeItem ? (activeItem === id || byId[activeItem].ancestors[0] === id) && !getIsSettingsPage() : false;
        const isTooltipOpen = !isVerticalNavPinned ? primaryNav.openTooltip === id : false;
        if (!label) return null;
        return /*#__PURE__*/_jsx(VerticalNavMenuItem, {
          id: id,
          label: label,
          path: path,
          shouldHighlight: shouldHighlight,
          onClick: onClick(id),
          onMouseEnter: path ? onMouseEnterLink({
            id
          }) : onMouseEnter({
            id
          }),
          closeTooltip: path ? onMouseLeaveLink : undefined,
          isSubmenuOpen: isSubmenuOpen,
          secondaryNavItems: children,
          onKeyDown: onKeyDown({
            id,
            hasSubmenu,
            hasParentMenu: false,
            shouldShiftFocusOnOpen: true
          }),
          setIsMouseMovingTowardsSubmenu: setIsMouseMovingTowardsSubmenu,
          isBookmarksMenu: isBookmarksMenu,
          isTooltipOpen: isTooltipOpen,
          isASecondaryMenuOpen: isASecondaryMenuOpen,
          isVerticalNavPinned: isVerticalNavPinned
        }, id);
      })
    }), displayBlockingElement ? /*#__PURE__*/_jsx(BlockingElement, {
      "aria-hidden": "true"
    }) : null]
  });
};
export default VerticalNavMenu;